/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

export interface RedirectJsoParameters {
    cNm?: string;
    env?: string;
    Plt: RedirectPlatform;
    gcV?: string;
    jTK?: string;
    idpId?: string;
    prv?: string;
    loginHint?: string;
    cmsId?: string;
}

export interface RedirectOptions {
    replace?: boolean;
    jso?: RedirectJsoParameters;
}

export enum RedirectPlatform {
    PC = 'pc',
    Mac = 'mac',
    Browser = 'browser'
}

export enum CommonDynamicRedirect {
    BaseUrl = 'https://www.nvidia.com/content/drivers/redirect.asp?language=',
    NetworkTroubleshoot = 'gfn_pc_net_help',
    RecommendedRoutersProductPage = 'gfn_pc_recommended_routers_product_page',
    GfnPcInfo = 'gfn_pc_info',
    GfnPcHelp = 'gfn_pc_help',
    GfnPcTermsOfSale = 'gfn_pc_terms_of_sale',
    TermsConditions = 'gfn_pc_terms_of_use',
    AccountTerms = 'nvidia_account_tos',
    OssLicenses = 'gfn_pc_oss',
    PrivacyPolicy = 'privacy_policy',
    PrivacyCenter = 'privacy_portal',
    AccountManagement = 'nvidia_account_management',
    SysRequirements = 'gfn_pc_sys_req',
    UdpBufferTips = 'gfn_pc_net_help_udp_socket',
    GfnPcUnSupportedRegion = 'gfn_pc_unsupported_region',
    GfnPcUnsupportedDevice = 'gfn_pc_unsupported_device',
    GfnPcUnsupportedDeviceAndroid = 'gfn_pc_unsupported_device_android',
    GfnPcUnsupportedBrowser = 'gfn_pc_unsupported_browser',
    GfnPcNetHelpFirewall = 'gfn_pc_net_help_firewall',
    GfnPcNetHelpWifi = 'gfn_pc_net_help_wifi',
    GfnPcHelpUpdate = 'gfn_pc_help_update',
    GfnPcHelpMissingDecoder = 'gfn_pc_help_missing_decoder',
    GfnServerStatus = 'gfn_pc_status',
    CookiePolicy = 'nvidia_cookie_policy',
    GfnPcRecommendedGamepads = 'gfn_pc_recommended_gamepads',
    GfnAccountManagementSSO = 'nvidia_account_management_default',
    GfnAccountManagement = 'nvidia_account_management_gfn',
    GfnAccountManagementDeleteAccount = 'nvidia_account_management_delete_account',
    GfnStorageManagement = 'gfn_pc_cloud_storage_details',
    GfnStorageReset = 'gfn_pc_cloud_storage_reset',
    GfnPcMembershipJoin = 'gfn_pc_membership',
    GfnPcMembershipUpgrade = 'gfn_pc_membership_upgrade',
    GfnPcMembershipDayPass = 'gfn_pc_membership_daypass',
    GfnPcCheckoutDayPass = 'gfn_pc_checkout_daypass',
    GfnPcRedeemActivationCode = 'gfn_pc_redeem_activation_code',
    GfnPcHelpKeyboardGamesUnavailable = 'gfn_pc_help_keyboard_games_unavailable',
    GfnPcHelpTvGamepadOnly = 'gfn_pc_help_tv_gamepad_only',
    GfnPcWaitList = 'gfn_pc_waitlist',
    GfnPcAccountNotActivated = 'gfn_pc_account_not_activated',
    GfnPcKeyboardTroubleshooting = 'gfn_pc_keyboard_troubleshooting',
    GfnPcCustomProfileLearnMore = 'gfn_pc_custom_profile_learn_more',
    GfnPcResolutionProfileUpscalingLearnMore = 'gfn_pc_resolution_upscaling_learn_more',
    GfnPcHdrUnsupportedSystemsLearnMore = 'gfn_pc_hdr_unsupported_systems_learn_more',
    GfnPcUnsupportedResolutionsLearnMore = 'gfn_pc_unsupported_resolutions_learn_more',
    GfnPcUnsupportedReflexLearnMore = 'gfn_pc_unsupported_reflex_learn_more',
    GfnPcHelpConnections = 'gfn_pc_help_connections',
    GfnPcOpportunityBlockerLearnMore = 'gfn_pc_ad_blocker_learn_more',
    GdnHomePage = 'gdn_home_page',
    GdnInvalidDeeplink = 'gdn_invalid_deeplink',
    GfnPcUdsLearnMore = 'gfn_pc_uds_learn_more',
    GfnPcAddPlaytime = 'gfn_pc_checkout_playtime',
    GfnPcAddStorage = 'gfn_pc_cloud_storage_upgrade',
    GfnPcViewPlaytime = 'nvidia_account_management_gfn_playsummary',
    GfnPcHowPlaytimeWorks = 'gfn_pc_help_playtime',
    GfnPcLimitedFeaturesGamePlay = 'gfn_pc_help_playtime_limited_features',
    GfnPcSafariBrowserLearnMore = 'gfn_pc_safari_browser_learn_more',
    GfnPcCloudGsyncLearnMore = 'gfn_pc_cloud_gsync_learn_more',
    GfnPcl4sLearnMore = 'gfn_pc_l4s_learn_more',
    GfnPcHudStreamingLearnMore = 'gfn_pc_hud_streaming_learn_more',
    GfnPcBrowserPushNotification = 'gfn_pc_help_browser_push_notification',
    GfnPcDownload = 'gfn_pc_download',
    GfnPcMembershipCatalogLearnMore = 'gfn_pc_membership_catalog',
    GFNPcUnsupportedOS = 'gfn_pc_unsupported_os',
    GfnPcSupport = 'gfn_pc_support',
    GfnPcHelpPaste = 'gfn_pc_help_paste',
    GfnPcHelpLinuxInstallation = 'gfn_pc_help_linux_installation',
    GfnOffDeviceLogin = 'gfn_off_device_login',
    GfnOffDeviceAccountLink = 'gfn_off_device_account_link',
    GfnPcHelpInstallToPlay = 'gfn_pc_help_install_to_play',
    GfnPcHelpVsync = 'gfn_pc_help_vsync',
    GfnCodecLearnMore = 'gfn_codec_learn_more',
    GfnPcDlssOverridesLearnMore = 'gfn_pc_dlss_overrides_learn_more',
    GfnPcHelpStorageAndIap = 'gfn_pc_help_storage_and_iap',
    GfnPcSoldOutNotifyMeFree = 'gfn_pc_soldout_notify_me_free',
    GfnPcSoldOutNotifyMePerformance = 'gfn_pc_soldout_notify_me_performance',
    GfnPcSoldOutNotifyMeUltimate = 'gfn_pc_soldout_notify_me_ultimate'
}

export const CommonRedirectShortLinksMap = {
    [CommonDynamicRedirect.PrivacyPolicy]: 'nvidia.com/privacy',
    [CommonDynamicRedirect.PrivacyCenter]: 'nvidia.com/privacy-center',
    [CommonDynamicRedirect.CookiePolicy]: 'nvidia.com/cookies',
    [CommonDynamicRedirect.GfnPcTermsOfSale]: 'gfn.link/memberterms',
    [CommonDynamicRedirect.TermsConditions]: 'gfn.link/terms',
    [CommonDynamicRedirect.AccountTerms]: 'nvidia.com/account/terms',
    [CommonDynamicRedirect.OssLicenses]: 'gfn.link/licenses',
    [CommonDynamicRedirect.GfnPcRecommendedGamepads]: 'gfn.link/gamepads',
    [CommonDynamicRedirect.GfnAccountManagement]: 'gfn.link/account',
    [CommonDynamicRedirect.GfnAccountManagementSSO]: 'account.nvidia.com',
    [CommonDynamicRedirect.GfnPcMembershipJoin]: 'gfn.link/join',
    [CommonDynamicRedirect.GfnPcMembershipUpgrade]: 'gfn.link/upgrade',
    [CommonDynamicRedirect.GfnPcMembershipDayPass]: 'gfn.link/daypass',
    [CommonDynamicRedirect.GfnPcCheckoutDayPass]: 'gfn.link/adddays',
    [CommonDynamicRedirect.GfnPcRedeemActivationCode]: 'gfn.link/redeem',
    [CommonDynamicRedirect.GfnServerStatus]: 'gfn.link/status',
    [CommonDynamicRedirect.GfnPcHelpTvGamepadOnly]: 'gfn.link/5258',
    [CommonDynamicRedirect.UdpBufferTips]: 'gfn.link/4535',
    [CommonDynamicRedirect.GfnPcNetHelpFirewall]: 'gfn.link/4566',
    [CommonDynamicRedirect.GfnPcNetHelpWifi]: 'gfn.link/4567',
    [CommonDynamicRedirect.GfnPcHelpUpdate]: 'gfn.link/4628',
    [CommonDynamicRedirect.GfnPcUnSupportedRegion]: 'geforcenow.com',
    [CommonDynamicRedirect.GfnPcHelpMissingDecoder]: 'gfn.link/4637',
    [CommonDynamicRedirect.GfnPcWaitList]: 'gfn.link/waitlist',
    [CommonDynamicRedirect.RecommendedRoutersProductPage]: 'gfn.link/routers',
    [CommonDynamicRedirect.NetworkTroubleshoot]: 'gfn.link/4504',
    [CommonDynamicRedirect.GfnPcKeyboardTroubleshooting]: 'gfn.link/5024',
    [CommonDynamicRedirect.GfnPcHelpPaste]: 'gfn.link/5364',
    [CommonDynamicRedirect.GfnPcUnsupportedDevice]: 'gfn.link/unsupported',
    [CommonDynamicRedirect.GfnPcUnsupportedBrowser]: 'gfn.link/unsupported',
    [CommonDynamicRedirect.GfnPcCustomProfileLearnMore]: 'gfn.link/5340',
    [CommonDynamicRedirect.GfnPcUnsupportedResolutionsLearnMore]: 'gfn.link/5388',
    [CommonDynamicRedirect.GfnPcHelpConnections]: 'gfn.link/5399',
    [CommonDynamicRedirect.GfnPcOpportunityBlockerLearnMore]: 'gfn.link/5464',
    [CommonDynamicRedirect.GfnPcAddPlaytime]: 'gfn.link/addtime',
    [CommonDynamicRedirect.GfnPcViewPlaytime]: 'gfn.link/playtime',
    [CommonDynamicRedirect.GfnPcHowPlaytimeWorks]: 'gfn.link/1200',
    [CommonDynamicRedirect.GfnPcLimitedFeaturesGamePlay]: 'gfn.link/1201',
    [CommonDynamicRedirect.GfnPcSafariBrowserLearnMore]: 'gfn.link/5495',
    [CommonDynamicRedirect.GfnPcInfo]: 'geforcenow.com',
    [CommonDynamicRedirect.GfnPcl4sLearnMore]: 'gfn.link/5522',
    [CommonDynamicRedirect.GfnPcHudStreamingLearnMore]: 'gfn.link/5697',
    [CommonDynamicRedirect.GfnPcBrowserPushNotification]: 'gfn.link/5221',
    [CommonDynamicRedirect.GfnPcHdrUnsupportedSystemsLearnMore]: 'gfn.link/5390',
    [CommonDynamicRedirect.GfnPcMembershipCatalogLearnMore]: 'gfn.link/5601',
    [CommonDynamicRedirect.GfnPcSupport]: 'gfn.link/support',
    [CommonDynamicRedirect.GfnPcHelpLinuxInstallation]: 'gfn.link/5337',
    [CommonDynamicRedirect.GfnOffDeviceLogin]: 'gfn.link/login',
    [CommonDynamicRedirect.GfnOffDeviceAccountLink]: 'gfn.link/connect',
    [CommonDynamicRedirect.GfnPcDownload]: 'gfn.link/download',
    [CommonDynamicRedirect.GfnPcHelpInstallToPlay]: 'gfn.link/5675',
    [CommonDynamicRedirect.GfnStorageManagement]: 'gfn.link/cloudstorage',
    [CommonDynamicRedirect.GfnStorageReset]: 'gfn.link/resetstorage',
    [CommonDynamicRedirect.GfnPcAddStorage]: 'gfn.link/addstorage',
    [CommonDynamicRedirect.GfnCodecLearnMore]: 'gfn.link/5824',
    [CommonDynamicRedirect.GfnPcHelpStorageAndIap]: 'gfn.link/5887',
    [CommonDynamicRedirect.GfnPcSoldOutNotifyMeFree]: 'gfn.link/free-notify',
    [CommonDynamicRedirect.GfnPcSoldOutNotifyMePerformance]: 'gfn.link/performance-notify',
    [CommonDynamicRedirect.GfnPcSoldOutNotifyMeUltimate]: 'gfn.link/ultimate-notify'
};

// "Delete account & data" resolves to different destinations depending on the user's login provider
export const DeleteAccountVanityUrl = {
    // Users signed in with an NVIDIA account
    Nvidia: 'account.nvidia.com/delete',
    // Users signed in through a partner/telco identity provide
    Partner: 'account.nvidia.com/partnerdelete'
} as const;
